import React from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useTooltip, WpTooltipOverlay } from 'Wpds/components/tooltip';
import Avatar from 'Shared/components/Avatar/Avatar';

import './AvatarsOfMembers.scss';

type Props = {
  members: Array<{
    id: number;
    fullName: string;
    email: string;
    avatar: {
      color: string;
      initials: string;
      profilePictureUrl: string;
    };
  }>;
  reverseOverlay?: boolean;
  maxMembers?: number;
  className?: string;
  inactiveUsersIds?: number[];
  additionalLabelForTooltip?: string;
};

function AvatarsOfMembers({
  members,
  maxMembers = 3,
  className = '',
  inactiveUsersIds = [],
  reverseOverlay = false,
  additionalLabelForTooltip = '',
}: Props) {
  const tooltip = useTooltip({ placement: 'top' });

  const isMoreMaxMembers = members.length > maxMembers;
  const additionalMembersLabel = isMoreMaxMembers ? `+${members.length - maxMembers + 1}` : null;

  const membersAvatars = () => {
    if (isMoreMaxMembers) return members.slice(0, maxMembers - 1).map(({ avatar, id }) => ({ avatar, id }));
    return members.map(({ avatar, id }) => ({ avatar, id }));
  };

  const avatarsTooltipLabel = members
    .map(({ fullName, email }) => fullName || email)
    .filter(n => n)
    .join(', ');

  return (
    <>
      <div className={`d-flex align-items-center ${className}`} ref={tooltip.targetRef}>
        {membersAvatars().map(({ avatar, id }, index) => (
          <div
            key={id}
            className={inactiveUsersIds.includes(id) ? 'avatar-member--inactive' : ''}
            style={{ marginLeft: index ? '-13px' : 0, zIndex: reverseOverlay ? maxMembers - index : index + 1 }}
          >
            <div style={{ opacity: inactiveUsersIds.includes(id) ? 0.3 : 1 }}>
              <Avatar avatar={avatar} height={28} />
            </div>
          </div>
        ))}
        {additionalMembersLabel && (
          <div
            className="additional-members d-flex justify-content-center align-items-center"
            key={uuidv4()}
            style={{ marginLeft: '-13px', zIndex: reverseOverlay ? 1 : maxMembers + 1 }}
          >
            {additionalMembersLabel}
          </div>
        )}
      </div>
      <WpTooltipOverlay context={tooltip}>
        {avatarsTooltipLabel}
        {additionalLabelForTooltip}
      </WpTooltipOverlay>
    </>
  );
}

export default AvatarsOfMembers;
